// The sign-in steps, rendered from the flow store. Shared by the /auth page
// and the guest's save sheet; only the redirect URLs and the DOM are web-only.

import type { Flow } from "@aybuben/client/auth/flow.ts";
import { useEffect, useId, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router";
import { useStore } from "zustand";

import { capture } from "../capture.ts";
import { useStrings, type UiKey } from "../i18n/index.tsx";
import { NO_CAPTURE } from "../posthog.ts";
import { COL } from "../theme.ts";
import { Btn } from "../ui/primitives.tsx";
import { AUTH_ERROR_KEY, errorKey } from "./copy.ts";
import { Divider, ErrLine, Field, GoogleButton, LinkBtn, OtpInput } from "./fields.tsx";

interface FlowBodyProps {
	flow: Flow;
	guest: boolean;
	/** Above the entry step's controls: the page's welcome, or the sheet's pitch. */
	entryHeader: ReactNode;
	initialEmail?: string;
	/** Guest only: end the guest session so an existing account can sign in. */
	onLeaveGuest?: (email: string | null) => Promise<void>;
}

export function FlowBody({
	flow,
	guest,
	entryHeader,
	initialEmail = "",
	onLeaveGuest,
}: FlowBodyProps) {
	const step = useStore(flow, (s) => s.step);
	switch (step.k) {
		case "entry":
			return (
				<EntryStep flow={flow} guest={guest} header={entryHeader} initialEmail={initialEmail} />
			);
		case "code":
			return <CodeStep flow={flow} email={step.email} />;
		case "leave":
			return <LeaveStep flow={flow} email={step.email} onLeaveGuest={onLeaveGuest} />;
		case "done":
			return null;
	}
}

const useErr = (flow: Flow): UiKey | null => {
	const err = useStore(flow, (s) => s.err);
	return err ? AUTH_ERROR_KEY[err] : null;
};

function Terms() {
	const t = useStrings();
	return (
		<p className="mt-6 text-center text-xs leading-relaxed" style={{ color: COL.mute }}>
			{t("auth.termsPrefix")}{" "}
			<Link to="/terms" style={{ color: COL.lapis }}>
				{t("auth.termsLink")}
			</Link>{" "}
			{t("auth.termsAnd")}{" "}
			<Link to="/privacy" style={{ color: COL.lapis }}>
				{t("auth.privacyLink")}
			</Link>
			.
		</p>
	);
}

interface EntryStepProps {
	flow: Flow;
	guest: boolean;
	header: ReactNode;
	initialEmail: string;
}

function EntryStep({ flow, guest, header, initialEmail }: EntryStepProps) {
	const t = useStrings();
	const errId = useId();
	const err = useErr(flow);
	const busy = useStore(flow, (s) => s.busy);
	const [email, setEmail] = useState(() => initialEmail || flow.getState().email);
	// Back from Google's page, the browser may restore this page as it was left:
	// busy, with the buttons disabled and nothing actually in flight.
	useEffect(() => {
		const onShow = (e: PageTransitionEvent) => {
			if (e.persisted) flow.getState().pageRestored();
		};
		globalThis.addEventListener?.("pageshow", onShow);
		return () => globalThis.removeEventListener?.("pageshow", onShow);
	}, [flow]);
	const onGoogle = () => {
		capture("oauth_started", { provider: "google", guest });
		const origin = globalThis.location.origin;
		void flow
			.getState()
			.google({ success: `${origin}/auth/success`, failure: `${origin}/auth/failure` });
	};
	const submit = (e: FormEvent) => {
		e.preventDefault();
		void flow.getState().submitEmail(email);
	};
	return (
		<>
			{header}
			<GoogleButton onClick={onGoogle} disabled={busy}>
				{t("auth.continueGoogle")}
			</GoogleButton>
			<Divider label={t("auth.or")} />
			<form onSubmit={submit} noValidate>
				<Field
					label={t("auth.email")}
					type="email"
					value={email}
					onChange={setEmail}
					autoComplete="email"
					errorId={err ? errId : undefined}
				/>
				<ErrLine id={errId} k={err} />
				<div className="mt-6">
					<Btn disabled={busy} color={COL.pom}>
						{busy ? t("auth.busy") : t("auth.continueEmail")}
					</Btn>
				</div>
			</form>
			{guest && (
				<LinkBtn onClick={() => flow.getState().haveAccount()}>
					{t("auth.guestHaveAccount")}
				</LinkBtn>
			)}
			<Terms />
		</>
	);
}

function CodeStep({ flow, email }: { flow: Flow; email: string }) {
	const t = useStrings();
	const errId = useId();
	const err = useErr(flow);
	const busy = useStore(flow, (s) => s.busy);
	const resendAt = useStore(flow, (s) => s.resendAt);
	const [code, setCode] = useState("");
	const [now, setNow] = useState(Date.now);
	const wait = Math.max(0, Math.ceil((resendAt - now) / 1000));
	const waiting = wait > 0;
	useEffect(() => {
		if (!waiting) return;
		const id = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(id);
	}, [waiting, resendAt]);
	// resendAt moves on every send, first or repeated.
	useEffect(() => {
		capture("code_sent");
	}, [resendAt]);
	// A rejected code is retyped from scratch, not edited.
	useEffect(() => {
		if (err) setCode("");
	}, [err]);
	return (
		<>
			<h1 className="mt-4 text-2xl font-extrabold">{t("auth.codeTitle")}</h1>
			<p className={`mt-2 text-sm leading-relaxed ${NO_CAPTURE}`} style={{ color: COL.mute }}>
				{t("auth.codeSent", { email })} {t("auth.codeSpam")}
			</p>
			<OtpInput
				label={t("auth.code")}
				value={code}
				onChange={setCode}
				onComplete={(c) => void flow.getState().submitCode(c)}
				disabled={busy}
				errorId={err ? errId : undefined}
			/>
			{busy && (
				<p role="status" className="mt-3 text-sm" style={{ color: COL.mute }}>
					{t("auth.checking")}
				</p>
			)}
			<ErrLine id={errId} k={err} />
			<LinkBtn onClick={() => void flow.getState().resend()} disabled={waiting || busy}>
				{waiting ? t("auth.resendIn", { s: wait }) : t("auth.resend")}
			</LinkBtn>
			<LinkBtn onClick={() => flow.getState().changeEmail()}>{t("auth.changeEmail")}</LinkBtn>
		</>
	);
}

interface LeaveStepProps {
	flow: Flow;
	email: string | null;
	onLeaveGuest?: (email: string | null) => Promise<void>;
}

function LeaveStep({ flow, email, onLeaveGuest }: LeaveStepProps) {
	const t = useStrings();
	const errId = useId();
	const [busy, setBusy] = useState(false);
	const [err, setErr] = useState<UiKey | null>(null);
	const leave = () => {
		if (busy || !onLeaveGuest) return;
		setBusy(true);
		setErr(null);
		onLeaveGuest(email)
			.catch((e: unknown) => setErr(errorKey(e)))
			.finally(() => setBusy(false));
	};
	return (
		<>
			<h1 className="mt-4 text-2xl font-extrabold">
				{email ? t("auth.leaveTitleExists") : t("auth.leaveTitle")}
			</h1>
			<p className={`mt-2 text-sm leading-relaxed ${NO_CAPTURE}`} style={{ color: COL.mute }}>
				{email ? t("auth.leaveBodyExists", { email }) : t("auth.leaveBody")}
			</p>
			<ErrLine id={errId} k={err} />
			<div className="mt-6">
				<Btn onClick={leave} disabled={busy} color={COL.pom}>
					{busy ? t("auth.busy") : t("auth.leaveConfirm")}
				</Btn>
			</div>
			<LinkBtn onClick={() => flow.getState().changeEmail()}>{t("auth.back")}</LinkBtn>
		</>
	);
}
